// Pure roster math for the accountant dashboard's Team Roster panel.
// MUST mirror the Team Roster panel on the Team Performance page
// (performance-detail.jsx agentStats). Year is by closing date, roster =
// active Team Leads + Agents, sorted by closed volume.
//
// Two separate axes, never collapsed into one (see utils/dealCredit.js):
//   - Units and volume go to exactly ONE agent per deal (dealCreditsAgent),
//     so the team never reads as having closed the same house twice.
//   - Dollars go to EVERY person actually paid on the deal
//     (commissionForAgent). The agent who made an in-house referral is paid
//     his fee even though the team lead owns the unit, and a team lead who
//     works his own deal is paid both slices instead of one.

import { dealCreditsAgent, commissionForAgent } from "./dealCredit";
import { parseLocalDate } from "./commandCenter";

export function rosterMembers(users) {
  return (users || []).filter(
    (u) =>
      u?.type !== 1 &&
      u?.type !== 2 &&
      (u?.role === "Admin" || u?.role === "Employee"),
  );
}

const isClosed = (d) => d.dealStage?.toLowerCase() === "closed";
const isLive = (d) =>
  !["closed", "cancelled", "expired"].includes(d.dealStage?.toLowerCase());

export function rosterStats(deals, users, year) {
  const inYear = (deals || []).filter(
    (d) =>
      d && d.closingdate && parseLocalDate(d.closingdate).getFullYear() === year,
  );

  return rosterMembers(users)
    .map((agent) => {
      // Units and volume: only the deals this agent is credited with.
      const credited = inYear.filter((d) => dealCreditsAgent(d, agent.id));
      const closed = credited.filter(isClosed);
      const pending = credited.filter(isLive);

      // Dollars: every deal that actually paid this agent, credited or not.
      // Widening this is the whole point — a referring agent's fee used to
      // vanish because the unit went to the team lead.
      const paid = inYear.filter((d) => commissionForAgent(d, agent.id) !== 0);
      const sumPay = (list) =>
        list.reduce((s, d) => s + commissionForAgent(d, agent.id), 0);

      return {
        user: agent,
        volume: closed.reduce((s, d) => s + Number(d.offerPrice || 0), 0),
        dealsClosed: closed.length,
        dealsActive: pending.length,
        gci: sumPay(paid.filter(isClosed)),
        // What's under contract but not yet banked. Ranking on this is how an
        // agent with a loaded pipeline stops reading as a straggler.
        pendingVolume: pending.reduce((s, d) => s + Number(d.offerPrice || 0), 0),
        pendingComm: sumPay(paid.filter(isLive)),
        // The deals behind the counts, so a click on the number can open the
        // actual list instead of leaving it a dead figure. Credited deals only,
        // so the list always matches the count sitting above it.
        closedDeals: closed,
        pendingDeals: pending,
      };
    })
    .sort((a, b) => b.volume - a.volume);
}

// ── RANKING ──────────────────────────────────────────────────────────────
// The roster ranks three ways: what an agent has banked (closed), what
// they're about to bank (pending), or both together — measured either in sale
// volume or in the commission the team actually earned (GCI).
//
// Pure and shared, so the Team Performance roster and the accountant
// dashboard's roster can never rank the same people differently.

const RANK_FIELDS = {
  volume: { closed: "volume", pending: "pendingVolume" },
  gci: { closed: "gci", pending: "pendingComm" },
};

// The single number a row is ranked on under one mode/metric pair.
export function rankValueOf(row, mode = "closed", metric = "volume") {
  const f = RANK_FIELDS[metric] || RANK_FIELDS.volume;
  const closed = Number(row?.[f.closed] || 0);
  const pending = Number(row?.[f.pending] || 0);
  if (mode === "pending") return pending;
  if (mode === "total") return closed + pending;
  return closed;
}

function orderBy(rows, mode, metric) {
  return rows
    .slice()
    .sort((a, b) => rankValueOf(b, mode, metric) - rankValueOf(a, mode, metric));
}

export function rankRoster(rows, { mode = "closed", metric = "volume" } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return [];

  const ordered = orderBy(list, mode, metric);

  // Movement is always measured against the CLOSED ranking — that's the board
  // everyone already knows, so "▲3" answers "who have we been under-reading?"
  const baseline = new Map();
  orderBy(list, "closed", metric).forEach((r, i) => baseline.set(r, i));

  const top = rankValueOf(ordered[0], mode, metric);

  return ordered.map((row, i) => {
    const value = rankValueOf(row, mode, metric);
    return {
      ...row,
      rank: i + 1,
      rankValue: value,
      // Positive = climbed relative to the closed board.
      delta: mode === "closed" ? 0 : baseline.get(row) - i,
      // Bar width. Guard the all-zero roster so no NaN reaches a style attr.
      share: top > 0 ? value / top : 0,
    };
  });
}
